// Hilfen für die Reiter Messages und Emails der Inbox (Runde 6, Agent 6D): Texte, Zeiten, Zustände. Ohne Oberfläche.
import type { SmsStatus, SmsZeile } from "../../api/smsEingang";
import { berlinTeile, gueltig, heute, tageZwischen, tagText } from "../../werkzeuge/zeit";

export type SmsFilter = "offen" | "ungelesen" | "fehlgeschlagen" | "geplant" | "alle";

export const SMS_FILTER: { wert: SmsFilter; label: string; titel: string }[] = [
  { wert: "offen", label: "Offen", titel: "Eingegangene SMS, die noch nicht erledigt sind, und fehlgeschlagene Sendungen" },
  { wert: "ungelesen", label: "Ungelesen", titel: "Eingegangene SMS, die noch niemand geöffnet hat" },
  { wert: "fehlgeschlagen", label: "Fehlgeschlagen", titel: "Gesendete SMS, die nicht angekommen sind" },
  { wert: "geplant", label: "Geplant", titel: "SMS, die später gesendet werden" },
  { wert: "alle", label: "Alle", titel: "Alle SMS, eingehend und ausgehend" },
];

export const STATUS_TEXT: Record<SmsStatus, string> = {
  empfangen: "Empfangen",
  geplant: "Geplant",
  sendet: "Wird gesendet",
  gesendet: "Gesendet",
  zugestellt: "Zugestellt",
  fehlgeschlagen: "Fehlgeschlagen",
  abgebrochen: "Abgebrochen",
};

/** Heute: „14:05“, gestern: „Gestern, 14:05“, sonst „13.10.2026“. */
export function nachrichtZeit(iso: string | null | undefined): string {
  if (!gueltig(iso)) return "";
  const { datum, zeit } = berlinTeile(iso);
  const tage = tageZwischen(heute(), datum);
  if (tage === 0) return zeit;
  if (tage === -1) return `Gestern, ${zeit}`;
  return tagText(datum);
}

/** Für geplante SMS: „Morgen, 09:00“ oder „Mo 20.10.2026, 09:00“. */
export function planZeit(iso: string | null | undefined): string {
  if (!gueltig(iso)) return "";
  const { datum, zeit } = berlinTeile(iso);
  return `${tagText(datum)}, ${zeit}`;
}

export function istUngelesen(s: SmsZeile): boolean {
  return s.richtung === "eingehend" && !s.gelesen;
}

/** Zeile, die noch etwas von jemandem will: eingehend und nicht erledigt, oder eine fehlgeschlagene Sendung. */
export function istOffen(s: SmsZeile): boolean {
  return !s.erledigt && (s.richtung === "eingehend" || s.status === "fehlgeschlagen");
}

/** Kürzel für den Namen der Gegenstelle: Lead, sonst Nummer. */
export function gegenstelle(s: SmsZeile): string {
  return s.lead_name || s.entfernte_anzeige || s.entfernte_nummer || "Unbekannt";
}

/** Passt die Zeile (nach einer lokalen Änderung) noch zum Filter? Sonst fällt sie aus der Liste. */
export function passtZumFilter(s: SmsZeile, f: SmsFilter): boolean {
  switch (f) {
    case "offen":
      return istOffen(s);
    case "ungelesen":
      return istUngelesen(s);
    case "fehlgeschlagen":
      return s.status === "fehlgeschlagen";
    case "geplant":
      return s.status === "geplant";
    default:
      return true;
  }
}

/** Parameter der Liste für einen Filter. */
export function filterParameter(f: SmsFilter): { nur_offen?: boolean; nur_ungelesen?: boolean; status?: SmsStatus[] } {
  switch (f) {
    case "offen":
      return { nur_offen: true };
    case "ungelesen":
      return { nur_ungelesen: true };
    case "fehlgeschlagen":
      return { status: ["fehlgeschlagen"] };
    case "geplant":
      return { status: ["geplant"] };
    default:
      return {};
  }
}

/** Kurzer Text für die Vorschau in der Zeile (Zeilenumbrüche werden Leerzeichen). */
export function einzeilig(text: string | null | undefined): string {
  return (text ?? "").replace(/\s+/g, " ").trim();
}
